import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { listarCuponsVinculadosProdutoLoja } from "services/lojaProduto";
import type { ICupomLoja } from "interfaces/loja";
import { LojaDataGrid } from "../../../common";
import { GridColDef } from "@mui/x-data-grid";
import { useMaterialUIController, setMessage } from "context";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { emptyStateSx } from "../produto-form.theme";

export interface CuponsPageProps {
  produtoId: string;
}

export default function CuponsPage({ produtoId }: CuponsPageProps) {
  const navigate = useNavigate();
  const [, dispatch] = useMaterialUIController();
  const [cupons, setCupons] = useState<ICupomLoja[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!produtoId || produtoId === "0") return;
    void load();
  }, [produtoId]);

  async function load() {
    if (!produtoId) return;
    setLoading(true);
    try {
      const result = await listarCuponsVinculadosProdutoLoja(produtoId);
      const data = result.data;
      const lista = Array.isArray(data)
        ? data
        : ((data as { data?: ICupomLoja[]; cupons?: ICupomLoja[] })?.data ??
          (data as { cupons?: ICupomLoja[] })?.cupons ??
          []);
      setCupons(lista);
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: error instanceof Error ? error.message : "Falha ao carregar cupons vinculados.",
      });
      setCupons([]);
    } finally {
      setLoading(false);
    }
  }

  const columns: GridColDef<ICupomLoja>[] = useMemo(
    () => [
      { field: "codigo", headerName: "Código", width: 130, flex: 0 },
      {
        field: "tipo",
        headerName: "Tipo",
        width: 120,
        renderCell: (p) => (
          <Chip
            label={p.row.tipo === "PORCENTAGEM" ? "Porcentagem" : "Valor fixo"}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        field: "valor",
        headerName: "Valor",
        width: 110,
        renderCell: (p) =>
          p.row.tipo === "PORCENTAGEM"
            ? `${p.row.valor}%`
            : `R$ ${Number(p.row.valor || 0).toFixed(2)}`,
      },
      {
        field: "subtotalMinimo",
        headerName: "Mínimo",
        width: 110,
        renderCell: (p) =>
          p.row.subtotalMinimo != null
            ? `R$ ${Number(p.row.subtotalMinimo).toFixed(2)}`
            : "—",
      },
      {
        field: "ativo",
        headerName: "Ativo",
        width: 90,
        renderCell: (p) => (
          <Chip
            label={p.row.ativo ? "Sim" : "Não"}
            size="small"
            color={p.row.ativo ? "success" : "default"}
          />
        ),
      },
      {
        field: "inicioEm",
        headerName: "Início",
        width: 100,
        renderCell: (p) =>
          p.row.inicioEm
            ? format(new Date(p.row.inicioEm), "dd/MM/yy", { locale: ptBR })
            : "-",
      },
      {
        field: "fimEm",
        headerName: "Fim",
        width: 100,
        renderCell: (p) =>
          p.row.fimEm
            ? format(new Date(p.row.fimEm), "dd/MM/yy", { locale: ptBR })
            : "-",
      },
      {
        field: "limiteUsoGlobal",
        headerName: "Limite global",
        width: 110,
        renderCell: (p) => p.row.limiteUsoGlobal ?? "-",
      },
      {
        field: "limiteUsoPorUsuario",
        headerName: "Limite/usuário",
        width: 120,
        renderCell: (p) => p.row.limiteUsoPorUsuario ?? "-",
      },
      {
        field: "usoTotal",
        headerName: "Usos",
        width: 80,
        renderCell: (p) => p.row.usoTotal ?? 0,
      },
    ],
    [],
  );

  return (
    <MDBox p={3}>
      <MDBox display="flex" justifyContent="flex-end" mb={2}>
        <MDButton
          size="small"
          variant="outlined"
          color="dark"
          onClick={() => navigate("/loja/cupons-loja")}
        >
          <Icon sx={{ mr: 0.5 }}>confirmation_number</Icon>
          Gerenciar cupons
        </MDButton>
      </MDBox>

      {!loading && cupons.length === 0 ? (
        <MDBox sx={emptyStateSx}>
          <LocalOfferOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="body2" color="text.secondary">
            Nenhum cupom vinculado a este produto.
          </MDTypography>
        </MDBox>
      ) : (
        <LojaDataGrid
          rows={cupons}
          columns={columns}
          loading={loading}
          getRowId={(r) => r.id}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
        />
      )}
    </MDBox>
  );
}
